import { Tooltip, Typography } from '@mui/material'
import { useMfrrCmBids, useMfrrConfig } from 'api/mfrr/mfrr.api'
import { ReactNode, useEffect, useMemo, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Datetime from 'utils/datetime/datetime'
import { getMfrrDeliveryWindowEnd } from '../MRFFCMTrading/MFRRCMTrading.helper'
import {
  getCMDeliveryDateStartTime,
  getCurrentMinuteMarker,
  getEAMClosingWindowEndTimestamp,
  getEAMClosingWindowStartTimestamp,
  getMarketCloseCountdown,
  getNextCMMarketCloseTimestamp,
  getNextEAMMarketCloseTimestamp,
  getRelativeDayLabel,
  MINUTE_IN_MS,
  parseTimeString,
  subscribeToMinuteBoundary,
} from './MFRRTradingDeadlineCounter.helper'
import styles from './MFRRTradingDeadlineCounter.module.less'

type MarketCloseSectionProps = {
  title: string
  hours: number
  minutes: number
  info: string
  isWarning: boolean
  warningTooltip: string
}

function MarketCloseSection({
  title,
  hours,
  minutes,
  info,
  isWarning,
  warningTooltip,
}: MarketCloseSectionProps): ReactNode {
  return (
    <Tooltip title={warningTooltip} disableHoverListener={!isWarning} disableFocusListener={!isWarning} arrow>
      <div className={styles.Deadline_Section}>
        <div className={styles.Deadline_TitleRow}>
          <span className={`${styles.Deadline_Dot} ${isWarning ? styles.Deadline_DotWarning : ''}`} />
          <Typography variant="body2" className={styles.Deadline_Title}>
            {title}
          </Typography>
        </div>
        <div className={styles.Deadline_Row}>
          <Typography
            variant="h1"
            className={`${styles.Deadline_Countdown} ${isWarning ? styles.Deadline_CountdownWarning : ''}`}
          >
            {hours}h {minutes}min
          </Typography>
          <Typography variant="body2" className={styles.Deadline_Info}>
            {info}
          </Typography>
        </div>
      </div>
    </Tooltip>
  )
}

type MFRRTradingDeadlineCounterProps = {
  onWarningChange?: (hasWarning: boolean) => void
}

export default function MFRRTradingDeadlineCounter({ onWarningChange }: MFRRTradingDeadlineCounterProps): ReactNode {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()

  const { data: mfrrConfig, isPending: isConfigLoading, isError: isConfigError } = useMfrrConfig(activeSystem?.id)
  const minuteMarker = useSyncExternalStore(subscribeToMinuteBoundary, getCurrentMinuteMarker, getCurrentMinuteMarker)
  const mfrrTradingDeadlineCounter = minuteMarker * 60_000
  const isConfigReady = !isConfigLoading && !isConfigError && !!mfrrConfig

  const cmTimeComponents = mfrrConfig?.cm_gate_closure_time
    ? parseTimeString(mfrrConfig.cm_gate_closure_time)
    : undefined
  const cmMarketCloseTimestamp = getNextCMMarketCloseTimestamp(
    mfrrTradingDeadlineCounter,
    cmTimeComponents,
    mfrrConfig?.cm_input_margin_closure_time
  )
  const cmLeadDays = Math.max(0, mfrrConfig?.cm_lead_days ?? 0)
  const cmDeliveryDateStart = getCMDeliveryDateStartTime(cmMarketCloseTimestamp, cmLeadDays)
  const cmDeliveryDateEnd = getMfrrDeliveryWindowEnd(cmDeliveryDateStart)

  const eamMarketCloseTimestamp = getNextEAMMarketCloseTimestamp(
    mfrrTradingDeadlineCounter,
    mfrrConfig?.eam_input_margin_closure_time
  )
  const eamClosingWindowStartTimestamp = getEAMClosingWindowStartTimestamp(
    eamMarketCloseTimestamp,
    mfrrConfig?.eam_gate_closure_minutes_before,
    mfrrConfig?.eam_input_margin_closure_time
  )
  const eamClosingWindowEndTimestamp = getEAMClosingWindowEndTimestamp(
    eamMarketCloseTimestamp,
    mfrrConfig?.eam_gate_closure_minutes_before,
    mfrrConfig?.eam_input_margin_closure_time
  )
  const eamDeliveryDateStart = Datetime.getStartOfDay(
    Datetime.toISOString(eamClosingWindowStartTimestamp)
  ) as ISODateTime
  const eamDeliveryDateEnd = getMfrrDeliveryWindowEnd(eamDeliveryDateStart)

  const systemIdForBids = mfrrConfig ? activeSystem?.id : undefined
  const { data: cmBidsForDeadline = [] } = useMfrrCmBids({
    systemId: systemIdForBids,
    market: 'cm',
    startTime: cmDeliveryDateStart,
    endTime: cmDeliveryDateEnd,
  })
  const { data: eamBidsForDeadline = [] } = useMfrrCmBids({
    systemId: systemIdForBids,
    market: 'eam',
    startTime: eamDeliveryDateStart,
    endTime: eamDeliveryDateEnd,
  })

  const hasCmUnlockedValues = useMemo(
    () => cmBidsForDeadline.length > 0 && !cmBidsForDeadline.every((bid) => bid.status === 'published'),
    [cmBidsForDeadline]
  )

  const hasEamUnlockedValues = useMemo(() => {
    const bidsInClosingWindow = eamBidsForDeadline.filter((bid) => {
      const bidStartMs = new Date(bid.start_time).getTime()
      return bidStartMs >= eamClosingWindowStartTimestamp && bidStartMs < eamClosingWindowEndTimestamp
    })
    return bidsInClosingWindow.length > 0 && !bidsInClosingWindow.every((bid) => bid.status === 'published')
  }, [eamBidsForDeadline, eamClosingWindowStartTimestamp, eamClosingWindowEndTimestamp])

  const isCmInWarningWindow =
    isConfigReady &&
    cmMarketCloseTimestamp > mfrrTradingDeadlineCounter &&
    cmMarketCloseTimestamp - mfrrTradingDeadlineCounter <= 2 * MINUTE_IN_MS
  const isEamInWarningWindow =
    isConfigReady &&
    eamMarketCloseTimestamp > mfrrTradingDeadlineCounter &&
    eamMarketCloseTimestamp - mfrrTradingDeadlineCounter <= 2 * MINUTE_IN_MS

  const isCmWarningActive = isCmInWarningWindow && hasCmUnlockedValues
  const isEamWarningActive = isEamInWarningWindow && hasEamUnlockedValues

  useEffect(() => {
    onWarningChange?.(isCmWarningActive || isEamWarningActive)
  }, [isCmWarningActive, isEamWarningActive, onWarningChange])

  if (!isConfigReady) {
    return (
      <div className={styles.Deadline_Container}>
        <Typography variant="body2" className={styles.Deadline_Message}>
          {isConfigLoading
            ? t('Loading mFRR configuration...')
            : isConfigError
              ? t('Failed to load mFRR configuration')
              : t('No deadline configuration set for this system')}
        </Typography>
      </div>
    )
  }

  const cmCountdown = getMarketCloseCountdown(cmMarketCloseTimestamp, mfrrTradingDeadlineCounter)
  const cmCloseTimeText = Datetime.toLocalTime(Datetime.toISOString(cmMarketCloseTimestamp), 'hour')
  const cmDeliveryDateText = Datetime.toLocalTime(
    Datetime.addHours(Datetime.toISOString(cmMarketCloseTimestamp), cmLeadDays * 24),
    'dayMonth'
  )
  const cmCloseDay = getRelativeDayLabel(cmMarketCloseTimestamp, mfrrTradingDeadlineCounter, t)
  const cmInfo = `${cmCloseDay} ${cmCloseTimeText} ${t('for')} ${cmDeliveryDateText}`

  const eamCountdown = getMarketCloseCountdown(eamMarketCloseTimestamp, mfrrTradingDeadlineCounter)
  const eamCloseDay = getRelativeDayLabel(eamMarketCloseTimestamp, mfrrTradingDeadlineCounter, t)
  const eamCloseTimeText = Datetime.toLocalTime(Datetime.toISOString(eamMarketCloseTimestamp), 'hour')
  const eamWindowStartText = Datetime.toLocalTime(Datetime.toISOString(eamClosingWindowStartTimestamp), 'hour')
  const eamWindowEndText = Datetime.toLocalTime(Datetime.toISOString(eamClosingWindowEndTimestamp), 'hour')
  const eamInfo = `${eamCloseDay} ${eamCloseTimeText} ${t('for')} ${eamWindowStartText}-${eamWindowEndText}`
  const warningTooltip = t('There are unlocked values that should be locked before market close')

  return (
    <div className={styles.Deadline_Container}>
      <MarketCloseSection
        title={t('CM market closes for editing')}
        hours={cmCountdown.hours}
        minutes={cmCountdown.minutes}
        info={cmInfo}
        isWarning={isCmWarningActive}
        warningTooltip={warningTooltip}
      />
      <div className={styles.Deadline_Divider} />
      <MarketCloseSection
        title={t('EAM market closes for editing')}
        hours={eamCountdown.hours}
        minutes={eamCountdown.minutes}
        info={eamInfo}
        isWarning={isEamWarningActive}
        warningTooltip={warningTooltip}
      />
    </div>
  )
}
